import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SlidersHorizontal, X, Check } from "lucide-react";
import { api } from "../../lib/api";
import { setMeta } from "../../lib/seo";
import { useStore } from "../../context/StoreContext";
import { catUrl } from "../../lib/categoryUrl";
import ProductCard from "../../components/storefront/ProductCard";
import { Reveal } from "../../components/storefront/Reveal";

const SORTS = [
  { id: "newest", label: "En Yeniler" },
  { id: "best_selling", label: "Çok Satanlar" },
  { id: "price_asc", label: "Fiyat: Düşükten Yükseğe" },
  { id: "price_desc", label: "Fiyat: Yüksekten Düşüğe" },
  { id: "name", label: "İsme Göre (A-Z)" },
];

export default function CatalogPage() {
  const { categorySlug, brandSlug } = useParams();
  const { categories } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "newest";
  const brands = searchParams.get("brands")?.split(",").filter(Boolean) || [];
  const minPrice = searchParams.get("min") || "";
  const maxPrice = searchParams.get("max") || "";
  const inStock = searchParams.get("stok") === "1";
  const kampanya = searchParams.get("kampanya") === "1";
  const katRoot = searchParams.get("kat") || "";
  const katSub = searchParams.get("alt") || "";
  const page = parseInt(searchParams.get("sayfa") || "1", 10);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, page: String(page), limit: "24" });
    if (q) params.set("q", q);
    const effCat = categorySlug || katSub || katRoot;
    if (effCat) params.set("category", effCat);
    if (brandSlug) params.set("brand", brandSlug);
    if (minPrice) params.set("min_price", minPrice);
    if (maxPrice) params.set("max_price", maxPrice);
    if (inStock) params.set("in_stock", "true");
    if (kampanya) params.set("campaign_only", "true");
    if (brands.length && !brandSlug) params.set("brand", brands[0]);
    api.get(`/products?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [q, sort, categorySlug, brandSlug, minPrice, maxPrice, inStock, kampanya, katRoot, katSub, page, brands.join(",")]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("sayfa");
    setSearchParams(next);
  };

  const title = data?.category?.name || data?.brand?.name || (q ? `"${q}" için sonuçlar` : "Tüm Ürünler");
  const activeFilterCount = (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) + (inStock ? 1 : 0) + (kampanya ? 1 : 0) + (katRoot ? 1 : 0) + brands.length;
  const crumbChain = useMemo(() => {
    if (!data?.category) return [];
    const flat = new Map();
    const walk = (nodes, parent) => nodes.forEach((n) => { flat.set(n.category_id, { ...n, _parent: parent }); walk(n.children || [], n); });
    walk(categories, null);
    const chain = [];
    let cur = flat.get(data.category.category_id) || data.category;
    while (cur) {
      chain.unshift(cur);
      cur = cur._parent || (cur.parent_id ? flat.get(cur.parent_id) : null);
    }
    return chain;
  }, [data, categories]);
  const katRootDoc = useMemo(() => categories.find((c) => c.slug === katRoot), [categories, katRoot]);

  // Sol menü: kullanıcının bulunduğu ANA kategorinin alt kategori navigasyonu.
  // Yaprak kategorideyken (ör. Dadya > Kadın Parfüm) kökün kardeş kategorileri gösterilir,
  // aktif kategori vurgulanır. Marka filtresi bu navigasyonun YERİNE GEÇMEZ, altta ayrı kalır.
  const navRoot = useMemo(() => {
    if (!categorySlug || !data?.category || !crumbChain.length) return null;
    const top = crumbChain[0];
    return categories.find((c) => c.category_id === top.category_id) || top;
  }, [categories, data, categorySlug, crumbChain]);
  const activePathIds = useMemo(() => new Set(crumbChain.map((c) => c.category_id)), [crumbChain]);

  useEffect(() => {
    const desc = data?.category?.seo_description || data?.category?.description || `${title} — BIGELDİ'de keşfedin.`;
    setMeta("description", desc);
    if (data?.category?.seo_keywords) setMeta("keywords", data.category.seo_keywords);
  }, [data, title]);

  const FilterPanel = (
    <div className="space-y-8">
      {categorySlug && navRoot && (navRoot.children?.length || 0) > 0 && (
        <div>
          <Link to={catUrl(navRoot)}
            className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-neutral-400 mb-3 block hover:text-amber-700 transition-colors"
            data-testid="sidebar-nav-root">
            {navRoot.name}
          </Link>
          <div className="space-y-1" data-testid="sidebar-subcategories">
            {navRoot.children.map((ch) => {
              const onPath = activePathIds.has(ch.category_id);
              const isCurrent = data?.category?.category_id === ch.category_id;
              return (
                <div key={ch.category_id}>
                  <Link to={catUrl(ch, navRoot.slug)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${onPath ? "text-amber-700 font-semibold bg-amber-50" : "text-neutral-600 hover:text-amber-700 hover:bg-[#FAF9F5]"}`}
                    data-testid={`sidebar-subcat-${ch.slug}`} aria-current={isCurrent ? "page" : undefined}>
                    {onPath && <Check size={13} className="shrink-0 text-amber-600" />}
                    {ch.name}
                  </Link>
                  {onPath && (ch.children?.length || 0) > 0 && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-amber-200 pl-2">
                      {ch.children.map((gc) => {
                        const gcOnPath = activePathIds.has(gc.category_id);
                        return (
                          <Link key={gc.category_id} to={catUrl(gc, navRoot.slug)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-colors ${gcOnPath ? "text-amber-700 font-semibold bg-amber-50" : "text-neutral-500 hover:text-amber-700 hover:bg-[#FAF9F5]"}`}
                            data-testid={`sidebar-subcat-${gc.slug}`}
                            aria-current={data?.category?.category_id === gc.category_id ? "page" : undefined}>
                            {gcOnPath && <Check size={12} className="shrink-0 text-amber-600" />}
                            {gc.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!categorySlug && (
        <div>
          <p className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-neutral-400 mb-3">Kategori</p>
          <select value={katRoot} onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set("kat", e.target.value); else next.delete("kat");
              next.delete("alt"); next.delete("sayfa");
              setSearchParams(next);
            }}
            className="w-full h-10 px-3 bg-white border border-[#E6E4DD] rounded-lg text-sm focus:outline-none focus:border-amber-600"
            data-testid="filter-category">
            <option value="">Tüm Kategoriler</option>
            {categories.map((c) => <option key={c.category_id} value={c.slug}>{c.name}</option>)}
          </select>
          {katRootDoc?.children?.length > 0 && (
            <select value={katSub} onChange={(e) => setParam("alt", e.target.value)}
              className="w-full h-10 px-3 mt-2 bg-white border border-[#E6E4DD] rounded-lg text-sm focus:outline-none focus:border-amber-600"
              data-testid="filter-subcategory">
              <option value="">Tüm Alt Kategoriler</option>
              {katRootDoc.children.flatMap((ch) => ch.children?.length ? [ch, ...ch.children] : [ch])
                .map((ch) => <option key={ch.category_id} value={ch.slug}>{ch.name}</option>)}
            </select>
          )}
        </div>
      )}
      {(data?.brands?.length || 0) > 1 && (data?.total || 0) > 0 && (
      <div className="border-t border-[#E6E4DD] pt-6">
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-neutral-400 mb-1">Filtreler</p>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Marka</p>
        <div className="space-y-2">
          {data?.brands?.map((b) => (
            <label key={b.brand_id} className="flex items-center gap-2.5 text-sm cursor-pointer group">
              <input type="checkbox"
                checked={brands.includes(b.slug) || brandSlug === b.slug}
                disabled={!!brandSlug}
                onChange={(e) => {
                  const next = e.target.checked ? [...brands, b.slug] : brands.filter((x) => x !== b.slug);
                  setParam("brands", next.join(","));
                }}
                className="accent-amber-600 w-4 h-4"
                data-testid={`filter-brand-${b.slug}`}
              />
              <span className="group-hover:text-amber-700 transition-colors">{b.name}</span>
            </label>
          ))}
        </div>
      </div>
      )}
      {(data?.total || 0) > 0 && (<>
      <div>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-neutral-400 mb-3">Fiyat Aralığı</p>
        <div className="flex items-center gap-2">
          <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setParam("min", e.target.value)}
            className="w-full h-10 px-3 bg-white border border-[#E6E4DD] rounded-lg text-sm focus:outline-none focus:border-amber-600" data-testid="filter-min-price" />
          <span className="text-neutral-400">—</span>
          <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setParam("max", e.target.value)}
            className="w-full h-10 px-3 bg-white border border-[#E6E4DD] rounded-lg text-sm focus:outline-none focus:border-amber-600" data-testid="filter-max-price" />
        </div>
      </div>
      <label className="flex items-center gap-2.5 text-sm cursor-pointer">
        <input type="checkbox" checked={inStock} onChange={(e) => setParam("stok", e.target.checked ? "1" : "")} className="accent-amber-600 w-4 h-4" data-testid="filter-in-stock" />
        Sadece stokta olanlar
      </label>
      <label className="flex items-center gap-2.5 text-sm cursor-pointer">
        <input type="checkbox" checked={kampanya} onChange={(e) => setParam("kampanya", e.target.checked ? "1" : "")} className="accent-amber-600 w-4 h-4" data-testid="filter-campaign" />
        Kampanyalı ürünler
      </label>
      </>)}
      {activeFilterCount > 0 && (
        <button onClick={() => setSearchParams(q ? new URLSearchParams({ q }) : {})} className="text-xs text-rose-600 font-semibold flex items-center gap-1" data-testid="filter-reset">
          <X size={13} /> Filtreleri temizle ({activeFilterCount})
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>{data?.category?.seo_title || `${title} | BIGELDİ`}</title>
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>

      {crumbChain.length > 0 && (
        <nav className="text-xs text-neutral-400 mb-4 flex items-center gap-2 flex-wrap" data-testid="catalog-breadcrumb">
          <Link to="/" className="hover:text-amber-700">Anasayfa</Link>
          {crumbChain.map((c, i) => (
            <span key={c.category_id} className="flex items-center gap-2">
              <span>/</span>
              {i < crumbChain.length - 1 ? (
                <Link to={catUrl(c, crumbChain[0]?.slug)} className="hover:text-amber-700">{c.name}</Link>
              ) : (
                <span className="text-neutral-600">{c.name}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      {data?.category?.banner_url && (
        <div className="relative rounded-3xl overflow-hidden mb-8 h-44 sm:h-60" data-testid="category-banner">
          <img src={data.category.banner_url} alt={data.category.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
          <div className="absolute bottom-0 p-6 sm:p-8">
            <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-amber-300 mb-1">Kategori</p>
            <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight text-white">{title}</h1>
          </div>
        </div>
      )}

      {data?.campaign_ribbons?.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="campaign-ribbons">
          {data.campaign_ribbons.map((r) => (
            <div key={r.campaign_id}
              className="bg-amber-600 text-white rounded-2xl px-5 py-3.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center shadow-lg shadow-amber-600/20"
              data-testid={`campaign-ribbon-${r.campaign_id}`}>
              <span className="font-display font-extrabold text-sm sm:text-base tracking-tight">{r.name}</span>
              {r.end_date && <span className="text-[11px] font-semibold bg-white/20 rounded-full px-2.5 py-0.5">{r.end_date} tarihine kadar</span>}
            </div>
          ))}
        </div>
      )}


      <div className="flex items-end justify-between mb-8">
        <div>
          {!data?.category?.banner_url && (
            <>
              <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-amber-700 mb-2">
                {data?.category ? "Kategori" : data?.brand ? "Marka" : "Katalog"}
              </p>
              <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight" data-testid="catalog-title">{title}</h1>
            </>
          )}
          <p className="text-sm text-neutral-500 mt-2">{data?.total ?? 0} ürün</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={sort} onChange={(e) => setParam("sort", e.target.value)}
            className="h-10 px-3 bg-white border border-[#E6E4DD] rounded-full text-sm focus:outline-none focus:border-amber-600"
            data-testid="sort-select">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 h-10 px-4 bg-[#121214] text-white rounded-full text-sm" data-testid="mobile-filter-btn">
            <SlidersHorizontal size={15} /> Filtrele          </button>
        </div>
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-60 shrink-0 sticky top-36 self-start" data-testid="filter-sidebar">{FilterPanel}</aside>
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square bg-[#F4F2EB] rounded-2xl animate-pulse-soft" />)}
            </div>
          ) : data?.items?.length === 0 ? (
            <div className="py-24 text-center text-neutral-400">
              {data?.category ? (
                <>
                  <p className="font-display font-bold text-xl text-neutral-500 mb-2" data-testid="empty-category-msg">Bu kategorideki ürünler yakında eklenecek</p>
                  <p className="text-sm mb-6">Bu kategoriyi şu an hazırlıyoruz. Diğer kategorilerimize göz atabilirsiniz.</p>
                  <Link to="/" className="inline-flex items-center gap-2 h-11 px-6 bg-[#121214] text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors" data-testid="empty-category-home-btn">
                    Ana Sayfaya Dön
                  </Link>
                </>
              ) : (
                <>
                  <p className="font-display font-bold text-xl text-neutral-500 mb-2">Sonuç bulunamadı</p>
                  <p className="text-sm">Farklı bir arama veya filtre deneyin.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {data.items.map((p, i) => <Reveal key={p.product_id} delay={Math.min(i, 7) * 0.04}><ProductCard product={p} index={i} /></Reveal>)}
              </div>
              {data.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12 flex-wrap" data-testid="pagination-wrap">
                  {/* Pencereli sayfalama: 1 … (aktif±2) … son — binlerce sayfada taşma olmaz */}
                  {(() => {
                    const totalPages = data.pages;
                    const windowPages = new Set([1, 2, totalPages - 1, totalPages, page - 2, page - 1, page, page + 1, page + 2]);
                    const pagesList = [...windowPages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
                    const out = [];
                    let prev = 0;
                    for (const n of pagesList) {
                      if (prev && n - prev > 1) {
                        out.push(<span key={`e${prev}`} className="text-neutral-400 text-sm px-1">…</span>);
                      }
                      out.push(
                        <button key={n}
                          onClick={() => setParam("sayfa", String(n)) || setSearchParams(new URLSearchParams({ ...Object.fromEntries(searchParams), sayfa: n }))}
                          className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${page === n ? "bg-[#121214] text-white" : "bg-white border border-[#E6E4DD] hover:border-amber-600"}`}
                          data-testid={`pagination-${n}`}>
                          {n}
                        </button>
                      );
                      prev = n;
                    }
                    return out;
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto bg-[#FAF9F5] rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-lg">Filtrele & Sırala</h2>
              <button onClick={() => setFiltersOpen(false)} aria-label="Kapat" data-testid="mobile-filter-close"><X size={20} /></button>
            </div>
            {FilterPanel}
            <button onClick={() => setFiltersOpen(false)} className="w-full h-12 mt-8 rounded-full bg-[#121214] text-white font-semibold" data-testid="mobile-filter-apply">
              Sonuçları Göster
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
